import {
  getPricingTierLabel,
  type LaunchState,
  type PricingTier,
  type StepId
} from './workflowShared';

interface FooterBarProps {
  currentStep: StepId;
  selectedSkillsCount: number;
  selectedModelCount: number;
  selectedPricingTier: PricingTier | null;
  selectedPricingTierAvailable: boolean;
  orderedSelectedSkillsCount: number;
  canAdvance: boolean;
  launchState: LaunchState;
  selectionLabelSingular?: string;
  selectionLabelPlural?: string;
  pricingFooterCopy?: string;
  launchButtonLabel?: string;
  onBack: () => void;
  onBackToSelection: () => void;
  onNext: () => void;
  onLaunchScan: () => void;
}

const getFooterCopy = ({
  currentStep,
  selectedSkillsCount,
  selectedModelCount,
  selectedPricingTier,
  selectedPricingTierAvailable,
  orderedSelectedSkillsCount,
  selectionLabelSingular = 'skill pack',
  selectionLabelPlural = 'skill packs',
  pricingFooterCopy
}: Pick<
  FooterBarProps,
  | 'currentStep'
  | 'selectedSkillsCount'
  | 'selectedModelCount'
  | 'selectedPricingTier'
  | 'selectedPricingTierAvailable'
  | 'orderedSelectedSkillsCount'
  | 'selectionLabelSingular'
  | 'selectionLabelPlural'
  | 'pricingFooterCopy'
>): string => {
  if (currentStep === 'agents') {
    return 'Start by choosing at least one AI model.';
  }

  if (currentStep === 'skills') {
    return `${selectedSkillsCount} ${selectedSkillsCount === 1 ? selectionLabelSingular : selectionLabelPlural} selected.`;
  }

  if (currentStep === 'codebase') {
    const modelText = `${selectedModelCount} model${selectedModelCount === 1 ? '' : 's'}`;
    const skillText = `${orderedSelectedSkillsCount} ${
      orderedSelectedSkillsCount === 1 ? selectionLabelSingular : selectionLabelPlural
    }`;

    return `${modelText} and ${skillText} ready.`;
  }

  if (pricingFooterCopy) {
    return pricingFooterCopy;
  }

  return selectedPricingTierAvailable
    ? `${getPricingTierLabel(selectedPricingTier)} scan ready.`
    : 'Free scan unavailable.';
};

function FooterBar({
  currentStep,
  selectedSkillsCount,
  selectedModelCount,
  selectedPricingTier,
  selectedPricingTierAvailable,
  orderedSelectedSkillsCount,
  canAdvance,
  launchState,
  selectionLabelSingular,
  selectionLabelPlural,
  pricingFooterCopy,
  launchButtonLabel = 'Launch Scan',
  onBack,
  onBackToSelection,
  onNext,
  onLaunchScan
}: FooterBarProps) {
  return (
    <footer className="footer-bar">
      <div className="footer-copy">
        {getFooterCopy({
          currentStep,
          selectedSkillsCount,
          selectedModelCount,
          selectedPricingTier,
          selectedPricingTierAvailable,
          orderedSelectedSkillsCount,
          selectionLabelSingular,
          selectionLabelPlural,
          pricingFooterCopy
        })}
      </div>

      <div className="footer-actions">
        {currentStep === 'agents' ? (
          <button className="secondary-button" type="button" onClick={onBackToSelection}>
            Back to selection
          </button>
        ) : (
          <button className="secondary-button" type="button" onClick={onBack}>
            Back
          </button>
        )}

        {currentStep !== 'pricing' ? (
          <button className="primary-button" type="button" disabled={!canAdvance} onClick={onNext}>
            Next
          </button>
        ) : (
          <button
            className="primary-button"
            type="button"
            disabled={!canAdvance || launchState === 'accepted'}
            onClick={onLaunchScan}
          >
            {launchState === 'submitting' ? 'Launching...' : launchButtonLabel}
          </button>
        )}
      </div>
    </footer>
  );
}

export default FooterBar;
